import { CustomFieldPrefixMap, SystemFieldPrefixMap } from '../../constants/forms/ticketFormFields';
import { refactorFieldOptions } from '../../hooks/ticketDetailPage/useFieldDefinitionListFromAgentForm';
import { Attributes, FieldDefinitionMap, IFieldDefinition, customFieldOptionsMap } from '../../model/types';

const fieldTypesWithAutoCompleteUserSearchFeature = [
    customFieldOptionsMap.MULTI_USER,
    customFieldOptionsMap.USER
] as string[];

export const dateTimeFields = [customFieldOptionsMap.DATE, customFieldOptionsMap.DATE_TIME] as string[];

export const fieldTypesWithOptions = [customFieldOptionsMap.SELECT, customFieldOptionsMap.MULTI_SELECT] as string[];

const fieldTypesWithArrayTypeValue = [
    // customFieldOptionsMap.ASSIGNEE,
    ...fieldTypesWithOptions,
    customFieldOptionsMap.TAG
] as string[];

export const hasAutoCompleteUserSearchFeature = (field: IFieldDefinition): boolean => {
    if (!field) {
        // console.debug('hasAutoCompleteUserSearchFeature: field is empty, will return false');
        return false;
    }
    return fieldTypesWithAutoCompleteUserSearchFeature.includes(field?.type);
};

export const isDateTimeTypeField = (field: Pick<IFieldDefinition, 'type'>): boolean => {
    if (!field) {
        // console.debug('isDateTimeTypeField: field is empty, will return false');
        return false;
    }

    return dateTimeFields.includes(field?.type);
};

export const isValueArrayTypeField = (field: Pick<IFieldDefinition, 'type'>): boolean => {
    if (!field) {
        // console.debug('isValueArrayTypeField: field is empty, will return false');
        return false;
    }

    return fieldTypesWithArrayTypeValue.includes(field?.type);
};

export const isSelectTypeField = (field: Pick<IFieldDefinition, 'type'>): boolean => {
    if (!field) {
        // console.debug('isSelectTypeField: field is empty, will return false');
        return false;
    }

    return field?.type === customFieldOptionsMap.SELECT;
};

export const isAssigneeTypeField = (field: Pick<IFieldDefinition, 'type'>): boolean => {
    if (!field) {
        // console.debug('isAssigneeTypeField: field is empty, will return false');
        return false;
    }

    return field?.type === customFieldOptionsMap.ASSIGNEE;
};

export const hasArrayTypeValueFeature = (field: Pick<IFieldDefinition, 'attributes'>): boolean => {
    if (!field) {
        // console.debug('hasArrayTypeValueFeature: field is empty, will return false');
        return false;
    }

    return field?.attributes?.includes(Attributes.ALLOW_NEW_VALUES);
};

export const isOptionAvailableType = (field: Pick<IFieldDefinition, 'type'>): boolean => {
    if (!field) {
        // console.debug('isSelectTypeField: field is empty, will return false');
        return false;
    }

    return fieldTypesWithOptions.includes(field.type);
};

export const collectFieldDefinition =
    (ticketFieldDefinitionMap: FieldDefinitionMap = {}) =>
    (fieldKey: string): IFieldDefinition => {
        if (!fieldKey) {
            // console.debug('collectFieldDefinition: fieldKey is empty, will return null');
            return null;
        }

        if (!(fieldKey in ticketFieldDefinitionMap)) {
            // console.debug(`useFieldDefinitionListFromAgentForm, fieldDefinition not found for key: ${fieldKey}`);
            return null;
        }

        return refactorFieldOptions<IFieldDefinition>(ticketFieldDefinitionMap[fieldKey]);
    };

export const getFieldKey = (field: IFieldDefinition): string => field?.key;

export const isOrganizationSystemField = (fieldDefinition: IFieldDefinition) => {
    return fieldDefinition?.key?.startsWith(SystemFieldPrefixMap.ORGANIZATION);
};
export const isUserSystemField = (fieldDefinition: IFieldDefinition) => {
    return fieldDefinition?.key?.startsWith(SystemFieldPrefixMap.USER);
};
/**
 * @deprecated
 * fieldDefinition coming with isSystemField info. use isSystemField instead of this
 */
export const isTicketSystemField = (fieldDefinition: IFieldDefinition) => {
    return fieldDefinition?.key?.startsWith(SystemFieldPrefixMap.TICKET);
};

/**
 * This TypeScript function checks if a given key is a custom field based on a provided custom field
 * type or a default list of system field prefixes.
 * @param {string} key - a string representing the key of a field
 * @param {CustomFieldPrefixMap} [customFieldType] - customFieldType is an optional parameter of type
 * CustomFieldPrefixMap. It is used to specify a specific custom field type to check for. If it is not
 * provided, the function will check for any custom field type.
 * @returns The function `isCustomField` returns a boolean value indicating whether the given `key`
 * string is a custom field or not. If the `customFieldType` parameter is provided, it checks if the
 * `key` string includes the prefix associated with that custom field type. If `customFieldType` is not
 * provided, it checks if the `key` string includes any of the prefixes defined in the `
 */
export const isCustomField = (key: string, customFieldType?: CustomFieldPrefixMap) => {
    if (customFieldType) {
        return key?.startsWith(CustomFieldPrefixMap[customFieldType]);
    } else {
        let isAnyInclude = false;
        isAnyInclude = Object.values(CustomFieldPrefixMap)
            .map((prefix) => key?.startsWith(prefix))
            .find((v) => v);
        return isAnyInclude;
    }
};
export const isSystemField = (key: string) => {
    return Object.values(SystemFieldPrefixMap).find((prefix) => key?.startsWith(prefix));
};
